// Training surface: the dedicated home for the synthetic Mannequin / Tailor-vs-LHS
// adaptive sampler. Pulled out of the ADB explorer so the aircraft/seed/symmetry
// configurator isn't omnipresent on the data-viewing page. Running a campaign
// here publishes it to the shared AdaptiveCampaign context, so the explorer can
// overlay the proposed points on its real plot.

import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAdaptiveCampaign } from "../adaptive-sampling/campaign-context";
import { Icons } from "../thread-v4/icons";
import { TailorMannequinAdbTutorialContent } from "./TailorMannequinAdbTutorial";
import { ThreadV5AdaptiveBuilder } from "./ThreadV5AdaptiveBuilder";
import type { SurfaceProps } from "./ThreadV5Surfaces";
import { TAILOR_MANNEQUIN_DEMO_AXES } from "./tailorMannequinDemoContract";
import "../../pages/TailorMannequinDemo.css";

const OUTPUT_OPTIONS = [
  { value: "CL", label: "CL — lift" },
  { value: "CD", label: "CD — drag" },
  { value: "CY", label: "CY — side force" },
  { value: "Cmx", label: "Cl — roll moment" },
  { value: "Cmy", label: "Cm — pitch moment" },
  { value: "Cmz", label: "Cn — yaw moment" },
];
const AXIS_OPTIONS = TAILOR_MANNEQUIN_DEMO_AXES.map((axis) => ({
  value: axis.id,
  label: `${axis.shortLabel} — ${axis.label}`,
}));

function validOutput(value: string | null): string {
  return OUTPUT_OPTIONS.some((item) => item.value === value) ? value! : "CL";
}

function validAxis(value: string | null, fallback: string): string {
  return AXIS_OPTIONS.some((item) => item.value === value) ? value! : fallback;
}

function parseDimensions(value: string | null): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= TAILOR_MANNEQUIN_DEMO_AXES.length
    ? parsed
    : TAILOR_MANNEQUIN_DEMO_AXES.length;
}

export function TrainingSurface({ projectId, projectName, branch, onOpenView }: SurfaceProps) {
  const { setSelection } = useAdaptiveCampaign();
  const [searchParams, setSearchParams] = useSearchParams();
  const [output, setOutput] = useState("CL");
  const [axisX, setAxisX] = useState("alpha");
  const [axisY, setAxisY] = useState("beta");
  const isTailorDemo = searchParams.get("tailorDemo") === "1";
  const demoOutput = validOutput(searchParams.get("tailorOutput"));
  const demoAxisX = validAxis(searchParams.get("tailorX"), "alpha");
  const requestedDemoAxisY = validAxis(searchParams.get("tailorY"), "beta");
  const demoAxisY = requestedDemoAxisY === demoAxisX
    ? AXIS_OPTIONS.find((axis) => axis.value !== demoAxisX)?.value ?? "beta"
    : requestedDemoAxisY;
  const demoMode = searchParams.get("tailorMode") === "result" ? "result" : "sequence";
  const demoCompare = searchParams.get("tailorCompare") === "1" || searchParams.get("tailorCompare") === "true";
  const demoReplay = searchParams.get("tailorReplay") === "1" || searchParams.get("tailorReplay") === "true";
  const demoDimensions = parseDimensions(searchParams.get("tailorDims"));
  const setDemoParam = useCallback((name: string, value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set(name, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const setDemoXAxis = useCallback((value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("tailorX", value);
      if (next.get("tailorY") === value || (!next.get("tailorY") && value === "beta")) {
        next.set("tailorY", AXIS_OPTIONS.find((axis) => axis.value !== value)?.value ?? "beta");
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  // Stable identity: the builder's onCampaignChange effect lists this in its
  // deps, so an inline closure would re-fire the effect every render → loop.
  const publishCampaign = useCallback(
    (campaign: { tailorId?: string; lhsId?: string; aircraftLabel?: string }) => {
      setSelection((selection) => ({
        ...campaign,
        projectId,
        adoptedInputs: selection.projectId === projectId ? selection.adoptedInputs : undefined,
      }));
    },
    [setSelection, projectId],
  );
  const adoptPoint = useCallback(
    (inputs: Record<string, number>) => {
      setSelection((selection) => ({ ...selection, projectId, adoptedInputs: inputs }));
      onOpenView("tailor");
    },
    [setSelection, projectId, onOpenView],
  );

  if (isTailorDemo) {
    return (
      <main className="v5-main v5-training-page v5-tailor-training-page" data-testid="thread-tailor-mannequin-demo">
        <header className="v5-training-head">
          <div className="v5-training-title">
            <span className="v5-eyebrow">Model / Adaptive sampling</span>
            <strong>Tailor builds the aircraft database — {projectName}</strong>
            <span className="v5-training-note">
              Tell Tailor what the database needs to capture, review the sampling policy, then watch the same project-native ADB surface build point by point.
            </span>
          </div>
          <div className="v5-training-controls">
            <div className="v5-training-choice">
              <span>Presentation</span>
              <div className="v5-toggle" role="group" aria-label="Tailor presentation mode">
                <button type="button" className={demoMode === "sequence" ? "is-active" : ""} aria-pressed={demoMode === "sequence"} onClick={() => setDemoParam("tailorMode", "sequence")}>Sequence</button>
                <button type="button" className={demoMode === "result" ? "is-active" : ""} aria-pressed={demoMode === "result"} onClick={() => setDemoParam("tailorMode", "result")}>End result</button>
              </div>
            </div>
            <div className="v5-training-choice">
              <span>Benchmark</span>
              <div className="v5-toggle" role="group" aria-label="LHS comparison visibility">
                <button type="button" className={!demoCompare ? "is-active" : ""} aria-pressed={!demoCompare} onClick={() => setDemoParam("tailorCompare", "0")}>Tailor only</button>
                <button type="button" className={demoCompare ? "is-active" : ""} aria-pressed={demoCompare} onClick={() => setDemoParam("tailorCompare", "1")}>Add LHS</button>
              </div>
            </div>
            <label className="v5-adb-compact-select">
              <span>Output</span>
              <select value={demoOutput} onChange={(event) => setDemoParam("tailorOutput", event.target.value)}>
                {OUTPUT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <label className="v5-adb-compact-select">
              <span>X</span>
              <select value={demoAxisX} onChange={(event) => setDemoXAxis(event.target.value)}>
                {AXIS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <label className="v5-adb-compact-select">
              <span>Y</span>
              <select value={demoAxisY} onChange={(event) => setDemoParam("tailorY", event.target.value)}>
                {AXIS_OPTIONS.filter((item) => item.value !== demoAxisX).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <button className="v5-text-button" type="button" onClick={() => onOpenView("tailor")}>
              <Icons.GridIcon size={13} />Open ADB Generator
            </button>
          </div>
        </header>

        <section className="tailor-thread-training" aria-label="Tailor adaptive ADB workflow">
          <TailorMannequinAdbTutorialContent
            output={demoOutput}
            dimensions={demoDimensions}
            focusXAxis={demoAxisX}
            focusYAxis={demoAxisY}
            presentationMode={demoMode}
            comparisonEnabled={demoCompare}
            replayMode={demoReplay}
            projectId={projectId}
            projectName={projectName}
            branch={branch}
          />
        </section>
      </main>
    );
  }

  return (
    <main className="v5-main v5-training-page">
      <header className="v5-training-head">
        <div className="v5-training-title">
          <span className="v5-eyebrow">Training</span>
          <strong>Adaptive sampler — {projectName}</strong>
          <span className="v5-training-note">
            Configure a Mannequin aircraft ADB and run Tailor against a cost-matched LHS baseline. Proposed points stream onto the ADB Generator&apos;s plots.
          </span>
        </div>
        <div className="v5-training-controls">
          <label className="v5-adb-compact-select">
            <span>Output</span>
            <select value={output} onChange={(event) => setOutput(event.target.value)}>
              {OUTPUT_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="v5-adb-compact-select">
            <span>X</span>
            <select value={axisX} onChange={(event) => setAxisX(event.target.value)}>
              {AXIS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="v5-adb-compact-select">
            <span>Y</span>
            <select value={axisY} onChange={(event) => setAxisY(event.target.value)}>
              {AXIS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <button className="v5-text-button" type="button" onClick={() => onOpenView("tailor")}>
            <Icons.GridIcon size={13} />Open ADB Generator
          </button>
        </div>
      </header>

      <ThreadV5AdaptiveBuilder
        selectedOutput={output}
        focusXAxis={axisX}
        focusYAxis={axisY}
        onAdoptPoint={adoptPoint}
        onCampaignChange={publishCampaign}
      />
    </main>
  );
}
